import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import Card from '../../components/Card';
import Button from '../../components/Button';
import DataTable, { Column } from '../../components/DataTable';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';
import {
  Package,
  PlusCircle,
  Edit,
  Trash2,
  Search,
  Filter,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Interfaces should match the backend models
interface Product {
  _id: string;
  name: string;
  description?: string;
  price: number;
  quantity: number;
  isActive: boolean;
  category?: string;
}

interface Kit {
  _id: string;
  name: string;
  description?: string;
  price: number;
  components?: Array<{ productId: Product; quantity: number }>;
  isActive: boolean;
}

type EquipmentItem = (Product & { type: 'product' }) | (Kit & { type: 'kit' });

const EquipmentManagement: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'product' | 'kit'>('all');

  const [products, setProducts] = useState<Product[]>([]);
  const [kits, setKits] = useState<Kit[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  const fetchEquipment = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Fetch both products and kits
      const [productsResponse, kitsResponse] = await Promise.all([
        apiService.products.getAll(),
        apiService.kits.getAll(),
      ]);

      // Adjust according to your actual API response structure
      setProducts((productsResponse as any).items || []);
      setKits((kitsResponse as any).items || []);

    } catch (err) {
      console.error("Error fetching equipment:", err);
      setError(err);
      toast.error("Erreur lors du chargement des équipements.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEquipment();
  }, []);

  const handleDelete = async (item: EquipmentItem) => {
    const { _id, type, name } = item;
    const confirmationMessage = `Êtes-vous sûr de vouloir supprimer ${type === 'product' ? 'le produit' : 'le kit'} \"${name}\" ?`;

    if (window.confirm(confirmationMessage)) {
      try {
        if (type === 'product') {
          await apiService.products.delete(_id);
          toast.success('Produit supprimé avec succès !');
        } else {
          await apiService.kits.delete(_id);
          toast.success('Kit supprimé avec succès !');
        }
        fetchEquipment(); // Refresh data
      } catch (err) {
        console.error(`Error deleting ${type}:`, err);
        toast.error(`Erreur lors de la suppression de l\'élément.`);
      }
    }
  };

  const allEquipment = useMemo(() => {
    let combined: EquipmentItem[] = [];
    if (typeFilter === 'all' || typeFilter === 'product') {
      combined.push(...products.map(p => ({ ...p, type: 'product' as const })));
    }
    if (typeFilter === 'all' || typeFilter === 'kit') {
      combined.push(...kits.map(k => ({ ...k, type: 'kit' as const })));
    }

    return combined.filter(item => 
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [products, kits, typeFilter, searchTerm]);

  const columns: Column<EquipmentItem>[] = [
    { header: 'Nom', accessor: 'name' },
    {
      header: 'Type',
      accessor: 'type',
      cell: (item: EquipmentItem) => (
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${ 
          item.type === 'product' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
        }`}>
          {item.type === 'product' ? 'Produit' : 'Kit'}
        </span>
      ),
    },
    {
      header: 'Prix', 
      accessor: 'price', 
      cell: (item: EquipmentItem) => 
        typeof item.price === 'number' ? `${item.price.toFixed(2)} €` : 'N/A' 
    },
    {
      header: 'Quantité/Composants', 
      cell: (item: EquipmentItem) => {
        if (item.type === 'product') {
          return item.quantity;
        }
        if (item.type === 'kit') {
          return `${item.components?.length || 0} comp.`;
        }
        return 'N/A';
      }
    },
    {
      header: 'Actif', 
      accessor: 'isActive', 
      cell: (item: EquipmentItem) => (item.isActive ? 'Oui' : 'Non')
    },
    {
      header: 'Actions',
      cell: (item: EquipmentItem) => (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/admin/equipments/edit/${item.type}/${item._id}`)}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button 
            variant="destructive"
            size="sm" 
            onClick={() => handleDelete(item)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Layout><div className="text-center py-8">Chargement des équipements...</div></Layout>;
  }

  if (error) {
    return <Layout><div className="text-center py-8 text-red-500">Erreur: {error.message || "Une erreur est survenue"}</div></Layout>;
  }

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Package className="w-8 h-8 mr-3 text-primary-600" />
            Gestion des Produits et Kits
          </h1>
          <Button onClick={() => navigate('/admin/equipments/add')}> 
            <PlusCircle className="w-5 h-5 mr-2" />
            Ajouter
          </Button>
        </div>

        <Card className="p-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Rechercher par nom ou description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter className="w-5 h-5 text-gray-400" />
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as 'all' | 'product' | 'kit')}
                  className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">Tous les types</option>
                  <option value="product">Produits</option>
                  <option value="kit">Kits</option>
                </select>
              </div>
            </div>
          </div>
        </Card>

        <DataTable columns={columns} data={allEquipment} keyField="_id" />
      </div>
    </Layout>
  );
};

export default EquipmentManagement;
